<template>
  <div v-if="loading" class="page-loading">
    <Loading />
  </div>
  <div v-if="isLibraryVisible">
    <div>
      <NotFoundContent
        v-if="libraryItems.length === 0"
        :message="$t('Library.libraryIsEmpty')"
      />
      <div v-else>
        <div class="strip fs-14 between-flex">
          <div class="w-25">
            <SearchAndFilter
              v-model="searchQuery"
              :nullable="true"
              :showInput="true"
              :showSelect="false"
              :placeholder="$t('Apps.searchByAppName')"
              @submitForm="handleSearchFilterChange"
            />
          </div>
          <div class="filter-container">
            <FilterComponent
              :sortCriteria="sortListCriteria"
              :sortOrder="sortListOrder"
              :sortingOptions="sortingListOptions"
              :isOrderOptions="false"
              :dafaultNoneOption="$t('List.name')"
              @filter-change="handleListFilterChange"
            />
            <FilterComponent
              :sortCriteria="sortCriteria"
              :sortOrder="sortOrder"
              :sortingOptions="sortingOptions"
              :orderOptions="orderOptions"
              :isOrderOptions="true"
              @filter-change="handleFilterChange"
            />
            <MultiSelectFilterComponent
              :available-filters="platformFilters"
              :initial-selected="selectedFilters"
              :placeholder-text="$t('Custom.allPlatforms')"
              @update:selected-filters="handlePlatformFilterChange"
            />
          </div>
        </div>
        <NotFoundContent
          v-if="filteredAndSortedItems.length === 0"
          message="Not found result."
        />
        <div v-else class="list-items-content">
          <TableComponent>
            <template v-slot:table-header>
              <tr>
                <th v-for="header in tableHeaders" :key="header.key">
                  {{ header.label }}
                </th>
              </tr>
            </template>
            <template v-slot:table-body>
              <tr
                v-for="(data, index) in filteredAndSortedItems"
                :key="data.item.id"
                :class="{ 'zebra-stripe': index % 2 === 0 }"
              >
                <td>{{ index + 1 }}</td>
                <td>
                  <img
                    :src="getItemIcon(data.item.icon)"
                    alt="App Icon"
                    class="app-icon"
                  />
                </td>
                <td>
                  <a :href="data.item.link" target="_blank">
                    <img
                      :src="getStoreImage(data.item.platformTypeId)"
                      alt="Platform Icon"
                      class="platform-icon"
                      title="Show Developer On Store"
                    />
                  </a>
                </td>
                <td>
                    <a
                      class="clickable-text"
                      :href="
                        $router.resolve({
                          name: 'App',
                          params: { storeId: data.item.storeId },
                        }).href
                      "
                      @click.stop="
                        handleLinkClick($event, {
                          name: 'App',
                          params: { storeId: data.item.storeId },
                        })
                      "
                      v-html="data.item.name || 'N/A'"
                      >
                    </a>
                    <div v-if="data?.item?.shortDescription" class="small-title">
                      {{ data?.item?.shortDescription}}
                    </div>
                </td>
                <td>
                  <a
                    v-if="data.item?.developer?.name"
                    :href="
                      $router.resolve({
                        name: 'Developer',
                        params: { storeId: data.item.developer.storeId },
                      }).href
                    "
                    @click.stop="
                      handleLinkClick($event, {
                        name: 'Developer',
                        params: { storeId: data.item.developer.storeId },
                      })
                    "
                    style="
                      cursor: pointer;
                      font-weight: bold;
                      color: var(--text-color);
                    "
                  >
                    {{ data.item.developer.name }}
                  </a>
                  <span v-else>-</span>
                </td>
                <td>{{ formatDate(data.item.lastUpdated) || "N/A" }}</td>
                <td>{{ formatDate(data.item.relasedDate) || "N/A" }}</td>
                <td>{{ data.item.price || "N/A" }}</td>
                <td>{{ data.item.rate || "N/A" }}</td>
                <td>{{ data.item.size || "N/A" }}</td>
                <td>
                  <template v-if="data.item.status === 1">
                    <span style="color: green">{{ $t("Custom.active") }}</span>
                  </template>
                  <template v-else>
                    <span style="color: red">{{ $t("Custom.inactive") }}</span>
                  </template>
                </td>
                <td>{{ formatDate(data.created_at) || "N/A" }}</td>
                <td>
                  <div class="action-buttons">
                    <div
                      class="action-button main"
                      @click.stop="handleAddToListModal(data)"
                    >
                      <span class="material-symbols-outlined notification-icon">
                        list_alt_add
                      </span>
                      <span>{{ $t("List.addToList") }}</span>
                    </div>
                    <div
                      class="action-button"
                      :class="{
                        green: data?.isNotification,
                        red: !data?.isNotification,
                      }"
                      @click.stop="
                        handleNotification(
                          data.item.id,
                          data.isNotification,
                          index
                        )
                      "
                    >
                      <template v-if="loadingIndex === index">
                        <span class="button-spinner"></span>
                      </template>
                      <template v-else>
                        <span
                          class="material-symbols-outlined notification-icon"
                        >
                          {{
                            data.isNotification
                              ? "notifications_active"
                              : "notification_add"
                          }}
                        </span>
                        <span>{{
                          data.isNotification ? "Is On" : "Is Off"
                        }}</span>
                      </template>
                    </div>
                    <div
                      class="action-button"
                      @click.stop="handleAreYouSureModal(data)"
                    >
                      <span class="material-symbols-outlined remove-icon"
                        >delete_sweep</span
                      >
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </TableComponent>
          <div class="pagination-wrapper">
            <PaginationComponent
              :meta="pagination"
              @page-changed="fetchLibraryItems"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <AppInfo
    v-if="isAppInfoVisible"
    :appData="appData"
    :appCountries="appCountries"
    :isBack="true"
    :defaultTab="defaultTab"
    :historyId="historyId"
    @get-app="fetchData"
    @close="handleBack"
    @showDeveloper="showDeveloper"
  />

  <DeveloperInfo
    v-if="isDeveloperLibraryInfoVisible"
    :developerData="developerData"
    :isBack="true"
    @close="handleBackDeveloperToLibrary"
  />

  <DeveloperInfo
    v-if="isDeveloperInfoVisible"
    :developerData="developerData"
    :isBack="true"
    @close="handleBackDeveloper"
  />

  <AreYouSureModal
    v-if="showModal"
    :isVisible="showModal"
    :title="modalTitle"
    :message="modalMessage"
    :itemId="selectedAppId"
    :confirmButtonLabel="$t('Custom.remove')"
    :cancelButtonLabel="$t('Custom.cancel')"
    @result="handleModalResult"
  />

  <NotificationComponent
    v-if="notification.show"
    :key="notification.key"
    :type="notification.type"
    :title="notification.title"
    :message="notification.message"
    @closed="!notification.show"
  />

  <Modal
    :isOpen="ShowAddToListModal"
    :modalTitle="modalTitle"
    :actionButton="appList.length"
    actionType="Add To List"
    @close="closeModal"
    @action="handleAddToListAction"
  >
    <NotFoundContent v-if="appList.length === 0" message="Not Found List" />
    <div v-else class="list-container">
      <label v-for="item in sortedAppList" :key="item.id" class="list-item">
        <input
          type="checkbox"
          :value="item.id"
          v-model="newSelectedLists"
          :disabled="isAlreadySaved(item.id)"
          @change="clearWarning"
        />
        {{ item.name }}
        <span v-if="isAlreadySaved(item.id)" class="saved-tag">
          (Already Added)
        </span>
      </label>
    </div>

    <p v-if="showWarning" class="warning-message">
      Please select at least one list.
    </p>
  </Modal>
</template>

<script>
import Loading from "@/components/user/layout/LoadingComponent.vue";
import NotFoundContent from "@/components/user/common/NotFoundContent.vue";
import ItemCard from "@/components/user/common/ItemCard.vue";
import SearchAndFilter from "@/components/user/common/SearchAndFilter.vue";
import AppInfo from "@/components/user/viewComponent/app/AppInfo.vue";
import Modal from "@/components/custom/modals/ModalComponent.vue";
import AreYouSureModal from "@/components/custom/modals/AreYouSureModal.vue";
import NotificationComponent from "@/components/custom/notifications/NotificationComponent.vue";
import FilterComponent from "@/components/user/common/FilterComponent.vue";
import MultiSelectFilterComponent from "@/components/user/common/MultiSelectFilterComponent.vue";
import DeveloperInfo from "@/components/user/viewComponent/developer/DeveloperInfo.vue";
import {
  parseSize,
  parsePrice,
  parseRate,
} from "@/ultis/user/functions/parseValue";
import PlatformTypeEnum from "@/enums/PlatformTypeEnum";
import { getDeveloperWithApps } from "@/api/developer.api";
import {
  getAppLibrary,
  removeAppFromAll,
  removeAppFromLibrary,
  updateNotification,
} from "@/api/appLibrary.api";
import { getApp, getAppCountries } from "@/api/app.api";
import TableComponent from "@/components/custom/table/TableComponent.vue";
import { storeImage } from "@/ultis/user/functions/storePlatform";
import {
  AddAppToMultipleLists,
  getAppList,
  getSavedList,
} from "@/api/appList.api";
import { getListByListType } from "@/api/list.api";
import ListTypeEnum from "@/enums/ListTypeEnum";
import PaginationComponent from "@/components/user/common/PaginationComponent.vue";

export default {
  name: "LibraryComponent",
  components: {
    Loading,
    NotFoundContent,
    ItemCard,
    SearchAndFilter,
    AppInfo,
    AreYouSureModal,
    NotificationComponent,
    FilterComponent,
    MultiSelectFilterComponent,
    DeveloperInfo,
    TableComponent,
    Modal,
    PaginationComponent,
  },
  data() {
    return {
      notification: {
        show: false,
        key: 0,
        type: "",
        title: "",
        message: "",
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null,
      },
      libraryItems: [],
      listByListType: [],
      appList: [],
      appSavedList: [],
      selectedLists: [],
      newSelectedLists: [],
      selectedApp: null,
      showWarning: false,
      ShowAddToListModal: false,
      searchQuery: "",
      sortOrder: "asc",
      appMenuId: null,
      loading: true,
      isAppInfoVisible: false,
      isLibraryVisible: true,
      appData: null,
      appCountries: null,
      url: null,
      showModal: false,
      selectedAppId: null,
      sortCriteria: "created_at",
      sortOrder: "desc",
      searchQuery: "",
      sortingOptions: [
        { value: "name", label: this.$t("Filter.name") },
        { value: "price", label: this.$t("Filter.price") },
        { value: "size", label: this.$t("Filter.size") },
        { value: "rating", label: this.$t("Filter.rating") },
        { value: "releaseDate", label: this.$t("Filter.relasedDate") },
        { value: "lastUpdated", label: this.$t("Filter.lastUpdated") },
        { value: "status", label: this.$t("Filter.status") },
        { value: "created_at", label: this.$t("Filter.addedAt") },
      ],
      orderOptions: [
        { value: "asc", label: this.$t("Filter.ascending") },
        { value: "desc", label: this.$t("Filter.descending") },
      ],
      showFilterDropdown: false,
      selectedFilters: [],
      platformFilters: PlatformTypeEnum.getAllOptions(),
      developerData: [],
      isDeveloperInfoVisible: false,
      isDeveloperLibraryInfoVisible: false,
      tableHeaders: [
        { key: "index", label: this.$t("Table.index") },
        { key: "icon", label: this.$t("Table.icon") },
        { key: "platform", label: this.$t("Table.platform") },
        { key: "name", label: this.$t("Table.name") },
        { key: "developer", label: this.$t("Table.developer") },
        { key: "lastUpdated", label: this.$t("Table.lastUpdated") },
        { key: "releaseDate", label: this.$t("Table.releaseDate") },
        { key: "price", label: this.$t("Table.price") },
        { key: "rate", label: this.$t("Table.rate") },
        { key: "size", label: this.$t("Table.size") },
        { key: "status", label: this.$t("Table.status") },
        { value: "added", label: this.$t("Table.addedAt") },
      ],

      sortListCriteria: null,
      sortListOrder: "",
      searchListQuery: "",
      sortingListOptions: [],
      loadingIndex: null,
    };
  },
  computed: {
    filteredAndSortedItems() {
      if (!this.libraryItems) return [];
      let items = [...this.libraryItems];
      return items;

      if (this.searchQuery) {
        items = items.filter((item) =>
          item.item.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }

      if (this.selectedFilters.length > 0) {
        items = items.filter((item) =>
          this.selectedFilters.includes(item.item.platformTypeId)
        );
      }

      if (this.sortListCriteria !== "All") {
        items = items.filter((item) => {
          return item.list.some(
            (list) => list.id.toString() === this.sortListCriteria
          );
        });
      }

      return items.sort((a, b) => {
        const modifier = this.sortOrder === "asc" ? 1 : -1;

        switch (this.sortCriteria) {
          case "name":
            return a.item.name.localeCompare(b.item.name) * modifier;
          case "price":
            return (
              (parsePrice(a.item.price) - parsePrice(b.item.price)) * modifier
            );
          case "size":
            return (parseSize(a.item.size) - parseSize(b.item.size)) * modifier;
          case "rating":
            return (parseRate(a.item.rate) - parseRate(b.item.rate)) * modifier;
          case "status":
            return (
              (parseRate(a.item.status) - parseRate(b.item.status)) * modifier
            );
          case "lastUpdated":
            const lastUpdatedA = new Date(a.item.lastUpdated).getTime();
            const lastUpdatedB = new Date(b.item.lastUpdated).getTime();
            if (isNaN(lastUpdatedA) || isNaN(lastUpdatedB)) return 0;
            return (lastUpdatedA - lastUpdatedB) * modifier;
          case "relasedDate":
            const releaseDateA = new Date(a.item.relasedDate).getTime();
            const releaseDateB = new Date(b.item.relasedDate).getTime();
            if (isNaN(releaseDateA) || isNaN(releaseDateB)) return 0;
            return (releaseDateA - releaseDateB) * modifier;
          case "addedAt":
            const createdAtA = new Date(a.created_at).getTime();
            const createdAtb = new Date(b.created_at).getTime();
            if (isNaN(createdAtA) || isNaN(createdAtb)) return 0;
            return (createdAtA - createdAtb) * modifier;
          default:
            return 0;
        }
      });
    },
    sortedAppList() {
      return [...this.appList].sort((a, b) => {
        const aSaved = this.isAlreadySaved(a.id);
        const bSaved = this.isAlreadySaved(b.id);
        if (aSaved !== bSaved) return aSaved - bSaved;
        return a.name.localeCompare(b.name);
      });
    },
  },
  async created() {
    Promise.all([
      await this.getListByListType(),
      await this.fetchLibraryItems(),
    ]);
    this.sortingListOptions = this.listByListType.map((list) => ({
      value: list.id.toString(),
      label: list.name,
    }));
    this.sortingListOptions.unshift({ value: null, label: "All List" });
  },
  methods: {
    async getListByListType() {
      try {
        const response = await getListByListType(ListTypeEnum.APP);
        if (response.data.status) {
          this.listByListType = response.data.data;
        }
      } catch (error) {}
    },
    handleLinkClick(event, routeData) {
      const url = this.$router.resolve(routeData).href;
      if (event.ctrlKey || event.metaKey || event.button === 2) {
        window.open(url, "_blank");
        return;
      }
      this.$router.push(routeData);
    },
    async handleAddToListModal(item) {
      this.loading = true;
      this.selectedApp = item.itemId;
      this.appList = this.listByListType.filter(
        (data) =>
          data.platformTypeId === item.item.platformType.id ||
          data.platformTypeId === PlatformTypeEnum.ALL
      );
      this.appSavedList = item.list;
      this.selectedLists = this.appSavedList.map((saved) => saved.listId);
      this.modalTitle = "Add To List" + " - " + item.item.name;
      this.ShowAddToListModal = true;
      this.loading = false;
    },
    async handleAddToListAction() {
      if (this.newSelectedLists.length === 0) {
        this.showWarning = true;
        return;
      }
      this.loading = true;
      await this.addAppToLists(this.selectedApp, this.newSelectedLists);
      this.loading = false;
      this.closeModal();
    },
    closeModal() {
      this.selectedLists = [];
      this.newSelectedLists = [];
      this.appList = [];
      this.appSavedList = [];
      this.selectedApp = null;
      this.ShowAddToListModal = false;
    },
    clearWarning() {
      this.showWarning = false;
    },
    isAlreadySaved(listId) {
      return this.appSavedList.some((saved) => saved.id === listId);
    },
    async addAppToLists(appId, listIds) {
      try {
        const response = await AddAppToMultipleLists(appId, listIds);
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.fetchLibraryItems();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async fetchAppList(appId) {
      try {
        const response = await getAppList(appId);

        if (response.data.status) {
          this.appList = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async getSavedList(appId) {
      if (this.appSavedList && this.appSavedList.length > 0) {
        return;
      }

      try {
        const response = await getSavedList(appId);
        if (response.data.status) {
          this.appSavedList = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    openLink(link) {
      if (link) {
        window.open(link, "_blank", "noopener,noreferrer");
      }
    },
    getStoreImage(platformTypeId) {
      return storeImage(platformTypeId);
    },
    getItemIcon(icon) {
      return icon || require("@/assets/img/developer/developer.png");
    },
    formatDate(date) {
      if (!date) return "";
      const locale = localStorage.getItem("localeTime") || "en-US";
      return new Date(date).toLocaleDateString(locale);
    },
    async handlePlatformFilterChange(filters) {
      this.selectedFilters = filters;
      await this.fetchLibraryItems();
    },
    async handleFilterChange({ criteria, order }) {
      this.sortCriteria = criteria;
      this.sortOrder = order;
      console.log(this.sortCriteria, this.sortOrder);
      await this.fetchLibraryItems();
    },
    async handleListFilterChange({ criteria, order }) {
      this.sortListCriteria = criteria;
      await this.fetchLibraryItems();
    },
    async handleSearchFilterChange() {
      await this.fetchLibraryItems();
    },
    async handleBack() {
      this.isLibraryVisible = true;
      this.isAppInfoVisible = false;
      this.appData = null;
      this.appCountries = null;
    },
    async handleBackDeveloper() {
      this.developerData = null;
      this.isDeveloperInfoVisible = false;
      this.isAppInfoVisible = true;
    },
    async handleBackDeveloperToLibrary() {
      this.developerData = null;
      this.isDeveloperLibraryInfoVisible = false;
      this.isLibraryVisible = true;
    },
    async fetchLibraryItems(page = 1) {
      this.loading = true;
      try {
        const response = await getAppLibrary(page, this.sortCriteria, this.sortOrder, this.selectedFilters, this.sortListCriteria, this.searchQuery);
        if (response.data.status) {
          this.libraryItems = response.data.data.data;
          this.pagination = response.data.data.meta;
        } else {
          this.showNotification(
            "hold",
            this.$t("Custom.hold"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async handleShow(storeId) {
      this.$router.push({
        name: "App",
        params: { storeId: storeId },
      });
    },
    async handleNotification(appId, isNotification, index) {
      this.loadingIndex = index;
      try {
        const response = await updateNotification(appId, !isNotification);
        if (response.data.status) {
          this.fetchLibraryItems();
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loadingIndex = null;
      }
    },
    async fetchData(url = this.url) {
      this.loading = true;
      try {
        await this.processAppLink(url);
        await this.getAppCountry();
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async processAppLink(url = this.url) {
      if (!url) return;

      try {
        const response = await getApp(url);

        if (response.data.status) {
          this.appData = response.data.data;
        } else {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
      }
    },
    async getAppCountry() {
      if (this.appCountries && this.appCountries.length > 0) {
        return;
      }
      try {
        const payload =
          this.appData.country.countryShortName === "US"
            ? { appId: this.appData.app.id }
            : { appId: this.appData.app.appId };
        const response = await getAppCountries(payload.appId);

        if (response.data.status) {
          this.appCountries = response.data.data.map((item) => ({
            ...item.country,
            appUrl: item.appUrl,
          }));
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async removeApp(appId) {
      this.loading = true;
      try {
        const response = await removeAppFromAll(appId);
        if (response.data.status) {
          await this.fetchLibraryItems();
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    handleAreYouSureModal(data) {
      this.modalTitle = this.$t("Custom.areYouSure");
      this.modalMessage =
        this.$t("Custom.doYouWantRemoveThis") +
        "<b>" +
        data.item.name +
        "</b> ? " +
        this.$t("Custom.thisActionCannotBeUndone") +
        "<p style='color: red'>" +
        this.$t("Custom.deleteFromAllLists") +
        " (" +
        data.list.length +
        ")" +
        "</p> ";
      this.selectedAppId = data.itemId;
      this.showModal = true;
    },
    setOpenAppMenu(menuId) {
      this.appMenuId = this.appMenuId === menuId ? null : menuId;
    },
    generateButtons(item) {
      return [
        {
          label: this.$t("Custom.show"),
          icon: "visibility",
          disable: false,
          action: () => this.handleShow(item.itemId),
        },
        {
          label: this.$t("Custom.remove"),
          icon: "remove",
          disable: false,
          action: () => {
            this.handleAreYouSureModal(item), this.setOpenAppMenu(item.itemId);
          },
        },
      ];
    },
    handleModalResult(result, appId) {
      this.showModal = false;
      if (result) {
        this.removeApp(appId);
      }
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        key: Date.now(),
        type,
        title,
        message,
      };
    },
    toggleFilterDropdown() {
      this.showFilterDropdown = !this.showFilterDropdown;
    },
    handleClickOutside(event) {
      if (
        this.$refs.filterDropdown &&
        !this.$refs.filterDropdown.contains(event.target)
      ) {
        this.showFilterDropdown = false;
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
.add-to-list-modal {
  padding: 15px;
}

.list-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 250px;
  overflow-y: auto;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  font-size: 14px;
  cursor: pointer;
}

.list-item input {
  cursor: pointer;
  transform: scale(1.2);
}

.list-item input:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.saved-tag {
  color: gray;
  font-size: 0.8rem;
}

.warning-message {
  color: red;
  font-size: 0.8rem;
  margin-top: 10px;
  text-align: center;
}
/** Filter */

/** */
.filter-container {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  align-items: center;
}

.strip {
  overflow-x: visible !important;
}
</style>
